import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import theme from '../../theme';
import Avatar from './Avatar';
import SignInModal from './SignInModal';
import config from '../../config';

const Navbar = ({ creator, user, donator, socialLinks, onSettingsClick, onDonatorLogout }) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const signInButtonRef = useRef(null);
  const navStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '60px',
    backgroundColor: 'rgba(10, 11, 13, 0.95)',
    backdropFilter: 'blur(10px)',
    borderBottom: `1px solid ${theme.colors.border.default}`,
    zIndex: theme.zIndex.sticky,
    display: 'flex',
    alignItems: 'center',
    padding: `0 ${theme.spacing[6]}`,
  };

  const leftSectionStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[4],
    flex: 1,
  };

  const centerSectionStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[4],
  };

  const rightSectionStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[4],
  };

  const creatorInfoStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[3],
  };

  const iconButtonStyles = {
    color: theme.colors.text.secondary,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing[2],
    borderRadius: theme.borderRadius.md,
    transition: `all ${theme.transitions.base}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const socialIconStyles = {
    ...iconButtonStyles,
    fontSize: '18px',
  };

  const badgeStyles = {
    backgroundColor: theme.colors.primary[900],
    color: theme.colors.primary[400],
    padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.medium,
    textTransform: 'uppercase',
  };

  const userDropdownStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
    padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
    backgroundColor: theme.colors.bg.card,
    borderRadius: theme.borderRadius.full,
    cursor: 'pointer',
    transition: `all ${theme.transitions.base}`,
  };

  return (
    <nav style={navStyles}>
      <div style={leftSectionStyles}>
        <div style={creatorInfoStyles}>
          <Avatar
            src={creator?.logo_url
              ? (creator.logo_url.startsWith('http') ? creator.logo_url : `${config.API_URL}${creator.logo_url}`)
              : `${window.location.hostname === 'mycastle.gg' || window.location.hostname === 'www.mycastle.gg' ? '/app' : ''}/average_matty.jpeg`}
            alt={creator?.name || "Creator"}
            size="sm"
          />
          <div>
            {creator?.name && (
              <h3 style={{ 
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.text.primary,
                margin: 0,
                marginBottom: '2px',
              }}>
                {creator.name}
              </h3>
            )}
            {creator?.isFounder && (
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[1] }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '2px',
                  backgroundColor: theme.colors.primary[500],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '7px',
                  fontWeight: 'bold',
                  color: '#fff'
                }}>
                  F
                </div>
                <span style={{
                  ...badgeStyles,
                  backgroundColor: 'transparent',
                  padding: 0,
                  fontSize: '10px',
                  color: theme.colors.text.secondary,
                  textTransform: 'uppercase',
                }}>
                  FOUNDER
                </span>
              </div>
            )}
          </div>
        </div>
        
        <div style={{ 
          width: '1px', 
          height: '30px', 
          backgroundColor: theme.colors.border.default,
          marginLeft: theme.spacing[4],
          marginRight: theme.spacing[4]
        }} />
        
        {/* Social media icons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
          {socialLinks && socialLinks.map((link) => {
            // Determine which icon to show based on the link URL
            const url = link.link_url.toLowerCase();
            let icon = null;
            
            if (url.includes('twitter.com') || url.includes('x.com')) {
              icon = (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
                </svg>
              );
            } else if (url.includes('instagram.com')) {
              icon = (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
                </svg>
              );
            } else if (url.includes('discord.com') || url.includes('discord.gg')) {
              icon = (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              );
            } else if (url.includes('youtube.com') || url.includes('youtu.be')) {
              icon = (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              );
            } else if (url.includes('twitch.tv')) {
              icon = (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                </svg>
              );
            } else if (url.includes('tiktok.com')) {
              icon = (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
              );
            } else if (url.includes('facebook.com') || url.includes('fb.com')) {
              icon = (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              );
            } else {
              // Generic link icon for unknown platforms
              icon = (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 6H6c-1.11 0-2 .89-2 2v10c0 1.11.89 2 2 2h10c1.11 0 2-.89 2-2v-4h2v4c0 2.21-1.79 4-4 4H6c-2.21 0-4-1.79-4-4V8c0-2.21 1.79-4 4-4h4v2zm6-2h6v6h-2V7.41l-8.29 8.3-1.42-1.42L18.59 6H16V4z"/>
                </svg>
              );
            }
            
            return icon ? (
              <a
                key={link.id}
                href={link.link_url}
                target="_blank"
                rel="noopener noreferrer"
                style={socialIconStyles}
                title={link.link_name}
                onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.text.primary}
                onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.text.secondary}
              >
                {icon}
              </a>
            ) : null;
          })}
        </div>
      </div>

      <div style={centerSectionStyles}>
        {/* Now empty - social icons moved to left */}
      </div>

      <div style={rightSectionStyles}>
        {donator ? (
          // Show donator name with dropdown when logged in
          <div style={{ position: 'relative' }}>
            <div
              style={userDropdownStyles}
              onClick={() => setShowDropdown(!showDropdown)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bg.hover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.bg.card}
            >
              <Avatar
                src={null}
                alt={donator.name || "Donator"}
                size="xs"
              />
              <span style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.text.primary
              }}>
                {donator.name}
              </span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ color: theme.colors.text.muted }}>
                <path d="M7 10l5 5 5-5z"/>
              </svg>
            </div>

            {/* Dropdown menu */}
            {showDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: theme.spacing[2],
                backgroundColor: theme.colors.bg.card,
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: theme.borderRadius.md,
                padding: theme.spacing[2],
                minWidth: '150px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 1000
              }}>
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    navigate('/my-account');
                  }}
                  style={{
                    width: '100%',
                    padding: theme.spacing[2],
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: theme.colors.text.primary,
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderRadius: theme.borderRadius.sm,
                    fontSize: theme.typography.fontSize.sm,
                    transition: `all ${theme.transitions.base}`
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bg.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  My Account
                </button>
                <button
                  onClick={async () => {
                    try {
                      await fetch(`${config.API_URL}/donators/logout`, {
                        method: 'POST',
                        credentials: 'include'
                      });
                      setShowDropdown(false);
                      if (onDonatorLogout) {
                        onDonatorLogout();
                      }
                      // Reload page to reset state
                      window.location.reload();
                    } catch (error) {
                      console.error('Logout failed:', error);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: theme.spacing[2],
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: theme.colors.text.primary,
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderRadius: theme.borderRadius.sm,
                    fontSize: theme.typography.fontSize.sm,
                    transition: `all ${theme.transitions.base}`
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bg.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Sign Out
                </button>
              </div>
            )}

          </div>
        ) : (
          // Show Sign In button when not logged in
          <div style={{ position: 'relative' }}>
            <button
              ref={signInButtonRef}
              onClick={() => setShowSignInModal(true)}
              style={{
                padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
                backgroundColor: theme.colors.primary[500],
                color: '#fff',
                border: 'none',
                borderRadius: theme.borderRadius.full,
                cursor: 'pointer',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.semibold,
                transition: `all ${theme.transitions.base}`
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primary[600]}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primary[500]}
            >
              Sign In
            </button>

            <SignInModal
              isOpen={showSignInModal}
              onClose={() => setShowSignInModal(false)}
              onLoginSuccess={(donator) => {
                console.log('Login successful:', donator);
              }}
              buttonRef={signInButtonRef}
            />
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;